import { useMemo } from 'react';
import { Briefcase, DollarSign, TrendingUp, Trophy } from 'lucide-react';
import type { Opportunity, PicklistValue } from '@/api/client';
import { fmtCurrency, stageColor } from '@/lib/cobalt-helpers';
import { useWonRecentOpportunities } from '@/api/hooks';
import { KPIStat } from '@/components/KPIStat';
import { StagePipelineBar, type PipelineSegment } from '@/components/StagePipelineBar';

interface Props {
  items: Opportunity[];
  stageOpts: PicklistValue[];
  assignedTo: 'me' | 'any';
  myResourceId: number | undefined;
}

/**
 * Drop-in top strip for the Opportunities page. Computes the 4 headline KPIs
 * + a stage-by-amount pipeline bar from whatever `items` it's given.
 *
 *   <OpportunitiesHeaderStrip items={items} stageOpts={picklists.data?.stage ?? []}/>
 */
export function OpportunitiesHeaderStrip({ items, stageOpts, assignedTo, myResourceId }: Props) {
  const stageMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of stageOpts) m.set(String(s.value), s.label);
    return m;
  }, [stageOpts]);

  const wonOwner  = assignedTo === 'me' ? myResourceId : undefined;
  const wonRecent = useWonRecentOpportunities(wonOwner);

  const kpi = useMemo(() => {
    const open     = items.filter((o) => o.status === 1);
    const sum      = (xs: Opportunity[]) => xs.reduce((s, o) => s + (o.amount ?? 0), 0);
    const weighted = items.reduce((s, o) => s + ((o.amount ?? 0) * (o.probability ?? 0) / 100), 0);

    const now        = Date.now();
    const ms30       = 30 * 24 * 60 * 60 * 1000;
    const wonItems   = wonRecent.data?.items ?? [];
    const last30     = wonItems
      .filter((o) => o.closedDate && now - new Date(o.closedDate).getTime() <= ms30)
      .reduce((s, o) => s + (o.amount ?? 0), 0);
    const prev30     = wonItems
      .filter((o) => {
        if (!o.closedDate) return false;
        const age = now - new Date(o.closedDate).getTime();
        return age > ms30 && age <= 2 * ms30;
      })
      .reduce((s, o) => s + (o.amount ?? 0), 0);

    const wonDelta     = last30 - prev30;
    const wonDeltaPct  = prev30 === 0 ? null : Math.round((wonDelta / prev30) * 100);
    const wonDeltaKind: 'up' | 'down' | 'flat' = wonDelta > 0 ? 'up' : wonDelta < 0 ? 'down' : 'flat';
    const wonDeltaStr  = wonDeltaPct === null
      ? undefined
      : `${wonDelta >= 0 ? '+' : ''}${wonDeltaPct}% vs prev 30d`;

    return {
      open: sum(open),
      weighted,
      count: open.length,
      last30,
      wonDeltaStr,
      wonDeltaKind,
    };
  }, [items, wonRecent.data]);

  const pipeline: PipelineSegment[] = useMemo(() => {
    const buckets = new Map<string, number>();
    for (const o of items) {
      const label = stageMap.get(String(o.stage)) ?? 'Unknown';
      if (/closed/i.test(label)) continue;
      buckets.set(label, (buckets.get(label) ?? 0) + (o.amount ?? 0));
    }
    return [...buckets.entries()]
      .filter(([, amount]) => amount > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([label, amount]) => ({
        label,
        amount,
        color: stageColor(label),
      }));
  }, [items, stageMap]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KPIStat
          label="Open pipeline"
          value={fmtCurrency(kpi.open, { compact: true })}
          icon={<Briefcase className="h-3 w-3"/>}
        />
        <KPIStat
          label="Weighted forecast"
          value={fmtCurrency(kpi.weighted, { compact: true })}
          icon={<TrendingUp className="h-3 w-3"/>}
        />
        <KPIStat
          label="Won (30 days)"
          value={fmtCurrency(kpi.last30, { compact: true })}
          delta={kpi.wonDeltaStr}
          deltaKind={kpi.wonDeltaKind}
          icon={<Trophy className="h-3 w-3"/>}
        />
        <KPIStat
          label="Open deals"
          value={kpi.count}
          icon={<DollarSign className="h-3 w-3"/>}
        />
      </div>

      {pipeline.length > 0 && (
        <section className="rounded-md border border-border bg-card p-4 shadow-sm">
          <div className="mb-2.5 flex items-baseline justify-between">
            <span className="text-[13px] font-semibold tracking-[-0.005em] text-foreground">
              Pipeline by stage
            </span>
            <span className="text-[11px] text-muted-foreground">Hover a segment to see the stage total</span>
          </div>
          <StagePipelineBar segments={pipeline}/>
        </section>
      )}
    </div>
  );
}
